/**
 * Turning the client's index into sections a person can navigate.
 *
 * Documents are grouped by **lane** — the direction of work they belong to (`ORG-2`) —
 * because that is the question people actually arrive with: "what have we decided about
 * pricing", "what is still open on the technical side". The folder a file happens to sit
 * in answers neither, since a pricing decision and a pricing research note live in
 * different directories by construction.
 *
 * The lane comes from the client, which reads it from the registry for decisions and from
 * frontmatter for everything else. Nothing is inferred here: a document with no lane is
 * shown as unfiled rather than guessed into a section, because a guess looks exactly like
 * an answer and this one would be wrong silently.
 */

const DOC_ROOT = "docs/"
const READABLE = /\.(md|csv|txt)$/i

/** What `docs.index` returns per document. */
export interface DocEntry {
  path: string
  title: string
  lane: string | null
  kind: string
  folder: string
  /** The document's own first paragraph — what a card shows under its title. */
  summary?: string
  meta?: Record<string, string>
  variants?: Record<string, { path: string; title: string }>
  missing?: string[]
}

/** A section of the menu, as the repository declares it in `.agent-lanes`. */
export interface Lane { id: string; label: string; hidden: boolean }

export interface DocItem {
  path: string
  /** The scannable token: `DEC-11`, `06`, a date. Empty when the name is all there is. */
  num: string
  name: string
  /** True when this document does not exist in the language being read. */
  untranslated?: boolean
}

/**
 * The telling of `entry` to show in `lang`, falling back to the original.
 *
 * A missing translation must never remove the document from the list. The reader came
 * looking for a decision, not for a language: hiding it answers a question nobody asked
 * and hides the gap that somebody has to close.
 */
export function inLanguage(entry: DocEntry, lang: string): {
  path: string; title: string; translated: boolean
} {
  const v = entry.variants?.[lang]
  if (v) return { path: v.path, title: v.title, translated: true }
  return { path: entry.path, title: entry.title, translated: false }
}

/** `uk` -> `Українська`, and unknown codes stay themselves rather than becoming a guess. */
export function languageName(code: string): string {
  try {
    const dn = new Intl.DisplayNames([code], { type: "language" })
    const name = dn.of(code)
    if (name && name !== code) return name.charAt(0).toUpperCase() + name.slice(1)
  } catch {
    /* an environment without Intl.DisplayNames, or a code it does not know */
  }
  return code.toUpperCase()
}

/**
 * The lanes, as the repository declares them.
 *
 * This used to be a constant here, which made "which sections does this project have" a
 * fact about the browser. It is a fact about the body of documents (`.agent-lanes`), so
 * the client reads it and this only holds the fallback for a client too old to send it.
 *
 * A **hidden** lane keeps filing its documents and loses its row in the menu. Deleting the
 * lane instead would orphan them: present in the repository, in no section, reachable from
 * nowhere.
 */
export const FALLBACK_LANES: Lane[] = [
  { id: "product", label: "Product", hidden: false },
  { id: "tech", label: "Technical", hidden: false },
  { id: "marketing", label: "Marketing", hidden: false },
  { id: "economics", label: "Economics", hidden: false },
  { id: "ops", label: "Operations", hidden: false },
  { id: "legal", label: "Legal", hidden: false },
]

let LANE_LABEL = new Map(FALLBACK_LANES.map(l => [l.id, l.label]))

/** Called once per index load, so labels elsewhere match the menu without being passed. */
export function rememberLanes(lanes: Lane[]): void {
  if (lanes.length) LANE_LABEL = new Map(lanes.map(l => [l.id, l.label]))
}

const UNFILED = "Unfiled"
const UNFILED_ID = "unfiled"

export function isDocument(path: string): boolean {
  return path.startsWith(DOC_ROOT) && READABLE.test(path)
}

/**
 * Directly in `docs/`, above every section.
 *
 * A decision registry indexes all the lanes, so it belongs to none of them — it is not
 * *unfiled*, it is deliberately higher. Without this distinction the project page showed
 * the registries twice: once above the sections where they belong, and once in `Unfiled`
 * as though nobody had got round to them.
 */
export function isAboveSections(path: string): boolean {
  return isDocument(path) && !path.slice(DOC_ROOT.length + 1).includes("/")
}

export function describe(entry: DocEntry, lang?: string): DocItem {
  const shown = lang ? inLanguage(entry, lang)
    : { path: entry.path, title: entry.title, translated: true }
  // The path of the telling being read, not of the original: this is what a click opens,
  // and using the original here silently serves English to somebody who chose Ukrainian.
  const path = shown.path
  const title = shown.title
  const untranslated = !shown.translated

  // `DEC-2 — Hosted service, local client, …` splits into the id and the sentence: the id
  // is what somebody scans a column for, the sentence is what they read.
  const decision = /^((?:DEC|ORG)-\d+)\s*[—–-]\s*(.+)$/.exec(title)
  if (decision) return { path, untranslated, num: decision[1], name: decision[2] }

  const file = path.split("/").pop() ?? path
  const stem = file.replace(READABLE, "")

  if (stem === "DECISIONS") return { path, untranslated, num: "", name: "Decision registry" }
  if (stem === "ORG-DECISIONS")
    return { path, untranslated, num: "", name: "Organisational registry" }

  const dated = /^(\d{4}-\d{2}-\d{2})[-_]/.exec(stem)
  if (dated) return { path, untranslated, num: dated[1], name: title }

  const numbered = /^(?:Step\s+)?(\d{1,3}[a-z]?)\s*[—–-]\s*(.+)$/.exec(title)
  if (numbered) return { path, untranslated, num: numbered[1], name: numbered[2] }

  return { path, untranslated, num: "", name: title }
}

export interface Category {
  id: string
  label: string
  count: number
}

/**
 * What the menu lists.
 *
 * Categories, not files: a sidebar of every document stops being navigable at the second
 * folder, and "which file" was never the question somebody arrives with. Every declared
 * lane is here whether or not it holds anything — an empty one is the fact that nothing
 * has been decided about pricing yet, and a section that disappears cannot report it.
 *
 * A **hidden** lane is left out of this list and out of nothing else. Its documents are
 * still filed, still counted, and still reachable from the project page, because a
 * document that exists and cannot be opened is worse than a menu row too many.
 */
export function categories(entries: DocEntry[], lanes: Lane[]): Category[] {
  const docs = entries.filter(e => isDocument(e.path) && !e.meta?.item)
  const out: Category[] = lanes
    .filter(l => !l.hidden)
    .map(l => ({ id: l.id, label: l.label, count: docs.filter(e => e.lane === l.id).length }))
  const known = new Set(lanes.map(l => l.id))
  const unfiled = docs.filter(e => (!e.lane || !known.has(e.lane))
    && !isAboveSections(e.path)).length
  if (unfiled) out.push({ id: UNFILED_ID, label: UNFILED, count: unfiled })
  return out
}

export function categoryLabel(id: string): string {
  return id === UNFILED_ID ? UNFILED : LANE_LABEL.get(id) ?? id
}

/** Where a new thing in this category is written. */
export function categoryParent(id: string): string {
  return LANE_LABEL.has(id) ? `docs/${id}` : "docs"
}

/**
 * A registry is data, so it is rendered as a table rather than as the raw text of a file.
 *
 * Deliberately small: split on commas outside quotes, unescape doubled quotes. These
 * files are written by our own tooling, and a full CSV grammar here would be a dependency
 * carrying edge cases nothing in this repository produces.
 */
export function parseCsv(text: string): { head: string[]; rows: string[][] } | null {
  const lines = text.split(/\r?\n/).filter(l => l.trim() !== "")
  if (lines.length < 2) return null
  const cells = lines.map(splitRow)
  const head = cells[0]
  const rows = cells.slice(1).filter(r => r.length === head.length)
  if (!rows.length) return null
  return { head, rows }
}

function splitRow(line: string): string[] {
  const out: string[] = []
  let cur = ""
  let quoted = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (quoted) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++ } else quoted = false
      } else cur += ch
    } else if (ch === '"') quoted = true
    else if (ch === ",") { out.push(cur); cur = "" }
    else cur += ch
  }
  out.push(cur)
  return out.map(c => c.trim())
}

const STATUSES = new Set([
  "accepted", "rejected", "proposed", "open", "parked", "superseded", "under-investigation",
])

export function statusClass(cell: string): string | null {
  const v = cell.trim().toLowerCase()
  if (!STATUSES.has(v)) return null
  return v === "accepted" ? "accepted"
    : v === "rejected" || v === "superseded" ? "rejected"
    : "open"
}

/** The lane cell in a registry row gets the same colour the sidebar uses for its section. */
export function laneLabel(value: string): string | null {
  return LANE_LABEL.get(value.trim().toLowerCase()) ?? null
}
